import { useCallback, useEffect, useState } from "react";
import { Lock, Key, Loader2, AlertTriangle, Check } from "lucide-react";
import api, { formatError } from "@/lib/api";

/**
 * AdminPinCard — lets the admin rotate the Back Office passcode from
 * inside the app, no redeploy required.
 *
 * Steve (Feb 2026): "I have individuals that have figured out that it
 * is very simple, only 111." So the immediate rotation flow is a first-
 * class in-app feature, not an env-var wrestle.
 *
 * Backend requires BOTH the admin allowlist email AND the current PIN
 * to rotate, so a leaked PIN alone can't lock the real admin out.
 */
export default function AdminPinCard() {
  const [config, setConfig] = useState(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/pin-config");
      setConfig(data);
    } catch (e) {
      setErr(formatError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const minLength = config?.min_length || 4;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk(false);
    if (next.length < minLength) {
      setErr(`New passcode must be at least ${minLength} characters.`);
      return;
    }
    if (next !== confirm) {
      setErr("New passcode and confirmation don't match.");
      return;
    }
    if (next === current) {
      setErr("New passcode must be different from the current one.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/pin", { current_pin: current, new_pin: next });
      setOk(true);
      setCurrent(""); setNext(""); setConfirm("");
      await load();
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bd-surface p-5" data-testid="admin-pin-card">
      <div className="flex items-center gap-2 mb-2 font-bold text-lg">
        <Key size={18} /> Back Office passcode
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--text-dim)" }}>
        Rotate the passcode that unlocks the Admin Back Office. Applies immediately —
        no redeploy needed. Everyone currently unlocked stays unlocked for their session.
      </p>

      {config?.is_default && (
        <div
          className="flex items-start gap-2 p-3 rounded mb-3"
          style={{
            background: "color-mix(in srgb, #ff3b30 12%, transparent)",
            border: "1px solid color-mix(in srgb, #ff3b30 35%, var(--border))",
            color: "#ff3b30",
          }}
          data-testid="admin-pin-default-warning"
        >
          <AlertTriangle size={14} className="mt-0.5" />
          <div className="text-xs">
            <strong>Your passcode is still the factory default (&ldquo;111&rdquo;).</strong>{" "}
            Anyone who guesses it can unlock the Back Office UI. Change it below now.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="flex flex-col gap-2">
        <label className="bd-label">Current passcode</label>
        <input
          type="password"
          autoComplete="current-password"
          className="bd-input"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder="Current passcode"
          data-testid="admin-pin-current"
          required
        />
        <label className="bd-label mt-2">New passcode</label>
        <input
          type="password"
          autoComplete="new-password"
          className="bd-input"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder={`At least ${minLength} characters`}
          data-testid="admin-pin-new"
          required
          minLength={minLength}
        />
        <label className="bd-label mt-2">Confirm new passcode</label>
        <input
          type="password"
          autoComplete="new-password"
          className="bd-input"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Type it again to confirm"
          data-testid="admin-pin-confirm"
          required
          minLength={minLength}
        />

        {err && (
          <div
            className="text-xs px-3 py-2 rounded"
            style={{ background: "color-mix(in srgb, #ff3b30 12%, transparent)", color: "#ff3b30" }}
            data-testid="admin-pin-error"
          >
            {err}
          </div>
        )}
        {ok && (
          <div
            className="text-xs px-3 py-2 rounded flex items-center gap-2"
            style={{ background: "color-mix(in srgb, #16a34a 12%, transparent)", color: "#16a34a" }}
            data-testid="admin-pin-ok"
          >
            <Check size={12} /> Passcode updated. Use the new one next time you unlock the Back Office.
          </div>
        )}

        <div className="flex items-center gap-2 mt-2">
          <button
            type="submit"
            disabled={busy}
            className="bd-btn flex items-center gap-2"
            data-testid="admin-pin-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
            Update passcode
          </button>
          <span className="text-[11px]" style={{ color: "var(--text-dim)" }}>
            {config?.admin_email && <>Signed in as <span className="bd-mono">{config.admin_email}</span></>}
          </span>
        </div>
      </form>
    </div>
  );
}
